// CSR-2196 (PB7.2) — the shapes the COI service returns for COI history.
//
// These were `typeof <table>.$inferSelect` until PB7 dropped the tables from
// this database. AMS has not written a row to any of them since 2026-08-07 and
// no longer reads them: every COI history surface is relayed from logrock-coi
// (`server/services/coiReadForward.ts`). But the relayed rows still need names —
// `client/src/lib/api.ts` types its queries on them and `SystemSettings.tsx`
// narrows on their status unions — so the types outlive the tables as
// hand-written declarations, and this file is where they live so nobody mistakes
// them for something drizzle still manages.
//
// They were transcribed from the compiler's resolved `$inferSelect` output at
// the drop, so they match what the service returns for every field AMS reads.
// The service's own tables have drifted AHEAD of these (it has since added
// columns AMS never had); that is fine and expected — a wire type describes what
// the consumer needs, and unknown extra fields are ignored at runtime.
//
// One inherited inaccuracy, deliberately preserved: the timestamp fields are
// typed `Date` because the drizzle inference said so, but they arrive over HTTP
// as ISO strings and nothing parses them back. That lie predates this change and
// correcting it is a behavior-affecting cleanup, not part of a table drop.

import { z } from "zod";

/** `coi_requests` — the COI Requests tab (SystemSettings) and its detail modal. */
export interface COIRequest {
  id: string;
  subject: string | null;
  status:
    | "pending_review"
    | "approved"
    | "completed"
    | "rejected"
    | "auto_sent"
    | "auto_skipped"
    | "awaiting_customer_reply"
    | "awaiting_carrier_data"
    | "awaiting_carrier_send"
    | "unhandled";
  inboundEmailId: string | null;
  agencyId: string | null;
  intercomEventId: string | null;
  intercomTicketId: string | null;
  source: "email" | "manual" | "intercom_webhook" | "coi_form";
  insuredId: string | null;
  policyId: string | null;
  matchConfidence: "high" | "medium" | "low" | "none" | null;
  parsedData: unknown;
  openaiData: unknown;
  customerName: string | null;
  customerEmail: string | null;
  messageBody: string | null;
  requester: "assure_assist" | "registry_monitoring" | "highway" | null;
  // The reason an automated send did not happen. Rendered as a label by the
  // COI Requests table; the service is the only writer.
  notSentReason:
    | "insured_has_no_active_policy"
    | "insured_has_multiple_active_policies"
    | "insured_has_notes"
    | "insured_has_policy_in_renewal_period"
    | "requester_not_mapped"
    | "insured_not_found"
    | "feature_flag_off"
    | "certificate_holder_not_found"
    | "asking_for_modifications"
    | "unmapped_request_ff_off"
    | "multiple_certificate_holders"
    | "has_attachments"
    | "incomplete_certificate_holder_data"
    | "now_certs_api_error"
    | "openai_extraction_failed"
    | "not_coi_related"
    | "carrier_not_in_rollout"
    | "auto_send_disabled"
    | "requested_assets_not_on_policy"
    | "unmapped_flow_error"
    | "intercom_ticket_failed"
    | "not_a_request"
    | "e2e_test"
    | "policy_has_no_coverage"
    | "insured_cancelled"
    | "reply_to_coi"
    | "excluded_phrase"
    | "renewal_term_requested"
    | null;
  createdAt: Date;
  updatedAt: Date;
  certificateHolderId: string | null;
  certificateHolderName: string | null;
  certificateHolderAddress: string | null;
  certificateHolderSource: "customer_reply" | null;
  additionalInsured: boolean;
  waiverOfSubrogation: boolean;
  sendCopyToInsured: boolean;
  includeAllVehicles: boolean;
  includeAllPowerUnits: boolean;
  includeAllTrailers: boolean;
  includeAllDrivers: boolean;
  coiTermCurrent: boolean | null;
  coiTermRenewal: boolean | null;
  description: string | null;
  recipientEmail: string | null;
  recipientName: string | null;
  coverageMins: unknown;
  coiIssuanceId: string | null;
  createdBy: string | null;
  reviewedBy: string | null;
  reviewedAt: Date | null;
  reviewNotes: string | null;
  lastApprovalError: string | null;
  lastApprovalAttemptAt: Date | null;
  policyNumber: string | null;
  policyCustomerName: string | null;
}

/** `coi_issuances` — one sent certificate. Backs the insured COIs tab, the
 *  activity timeline and the policy Activities tab's "COI sent" card. */
export interface COIIssuance {
  id: string;
  errorMessage: string | null;
  insuredId: string;
  certificateHolderId: string | null;
  certificateHolderName: string;
  additionalInsured: boolean;
  waiverOfSubrogation: boolean;
  description: string | null;
  recipientEmail: string | null;
  recipientName: string | null;
  documentId: string | null;
  insuredName: string | null;
  coverageLimits: unknown;
  additionalInfo: string[] | null;
  deliveryMethod: string;
  redirectedRecipientEmail: string | null;
  issuedBy: string | null;
  issuedAt: Date;
  syncStatus: string | null;
  deliveryStatus: string;
  sentAt: Date | null;
  failedAt: Date | null;
  sendOrigin: string | null;
  sendStartedAt: Date | null;
  syncTriggered: boolean | null;
  // Audit ids naming rows in the AMS database (loss payees and waivers stay
  // AMS-owned per CSR-2198). Never foreign keys on the service side.
  lossPayeeId: string | null;
  waiverOfSubrogationId: string | null;
  sendConfig: unknown;
}

/** `intercom_events` — the Intercom Events tab. */
export interface IntercomEvent {
  id: string;
  errorMessage: string | null;
  receivedAt: Date;
  notificationId: string;
  itemType: "conversation" | "ticket" | null;
  itemId: string | null;
  contactId: string | null;
  topic: string | null;
  sourceType: string | null;
  eventData: unknown;
  agencyId: string | null;
  processingStatus:
    | "received"
    | "processing"
    | "processed"
    | "failed"
    | "skipped";
  processedAt: Date | null;
}

/** `inbound_emails` — three rows ever existed (2026-07-30), all pre-cutover.
 *  Kept because `client/src/lib/api.ts` still names the type. */
export interface InboundEmail {
  id: string;
  fromEmail: string;
  fromName: string | null;
  toEmail: string | null;
  subject: string | null;
  textBody: string | null;
  htmlBody: string | null;
  ccEmails: unknown;
  messageId: string | null;
  inReplyTo: string | null;
  references: string | null;
  attachments: unknown;
  rawPayload: unknown;
  status: "received" | "processing" | "processed" | "failed";
  errorMessage: string | null;
  receivedAt: Date;
}

// ============================================================================
// CSR-2197 (PB7.3) — certificate holders
// ============================================================================
//
// Same story one phase later: `certificate_holders` left this database (the
// service has owned it since the CSR-2142 seam flipped; AMS's copy froze
// 2026-08-13), and these shapes outlive the table because the relay routes and
// the client still name them. `insured_additional_insureds` dropped too, but
// its `$inferSelect` type had NO consumers — the UI reads the storage-defined
// `InsuredAdditionalInsuredLink` instead — so nothing of it survives here.
//
// The two zod validators are here as well, hand-written to the exact runtime
// shape drizzle-zod used to derive (verified by printing the live schemas
// before the drop). They still gate the create/PATCH relay routes: the 400
// bodies are part of the /api contract, and the PATCH schema's omit list is
// the mass-assignment guard (CSR-745 S-001). The service re-validates with its
// own identical schema, so these must not drift looser than the service's.

/** `certificate_holders` — the holders page, pickers, and Companies tab. */
export interface CertificateHolder {
  id: string;
  code: string | null;
  name: string;
  address: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  contactEmail: string | null;
  contactName: string | null;
  contactPhone: string | null;
  requiresAdditionalInsured: boolean;
  requiresWaiverOfSubrogation: boolean;
  defaultDescription: string | null;
  // The min* fields are numeric columns service-side; drizzle maps numeric to
  // string, and the client renders them as strings. Preserved as-is.
  minGeneralLiability: string | null;
  minAutoLiability: string | null;
  minCargo: string | null;
  minUmbrella: string | null;
  minWorkersComp: string | null;
  usageCount: number;
  lastUsedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  archivedAt: Date | null;
  sourceId: string | null;
}

/** POST body for the create relay. `id` is required — holder ids are minted
 *  app-side (`CH-…`), not by the database. */
export const insertCertificateHolderSchema = z.object({
  id: z.string(),
  code: z.string().nullable().optional(),
  name: z.string(),
  address: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  contactEmail: z.string().nullable().optional(),
  contactName: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  requiresAdditionalInsured: z.boolean().optional(),
  requiresWaiverOfSubrogation: z.boolean().optional(),
  defaultDescription: z.string().nullable().optional(),
  minGeneralLiability: z.string().nullable().optional(),
  minAutoLiability: z.string().nullable().optional(),
  minCargo: z.string().nullable().optional(),
  minUmbrella: z.string().nullable().optional(),
  minWorkersComp: z.string().nullable().optional(),
  // int4 bounds, matching what drizzle-zod attached to integer() — review
  // caught the transcription dropping them, which would have loosened the
  // frozen POST contract.
  usageCount: z.number().min(-2147483648).max(2147483647).int().optional(),
  lastUsedAt: z.date().nullable().optional(),
  archivedAt: z.date().nullable().optional(),
});

export type InsertCertificateHolder = z.infer<typeof insertCertificateHolderSchema>;

// PATCH-body validator for `/api/certificate-holders/:id` (CSR-745 S-001).
// Strips every server-managed or immutable field so authenticated users
// cannot mass-assign:
//   - `id`           — primary key; immutable.
//   - `code`         — UNIQUE service-side, shared with the 3 mapped-requester
//                      seed rows; immutable post-create.
//   - `usageCount`   — server-managed; drives picker autocomplete ordering and
//                      list sort. Historical counts only since CSR-2181.
//   - `lastUsedAt`   — server-managed, paired with usageCount.
// Derived by the same omit().partial() the drizzle-zod original used, so the
// double-optional it produced is preserved rather than "cleaned up".
export const updateCertificateHolderSchema = insertCertificateHolderSchema
  .omit({ id: true, code: true, usageCount: true, lastUsedAt: true })
  .partial();
